import Board, { Stone } from './Board';
import { deepCopy } from '@/utils';

export default class Game {
  #playableCacheMask;

  constructor() {
    this.id = null;
    this.revision = 0;
    this.players = [];
    this.status = 'in-progress';
    this.winner = null;
    this.countResult = null;
    this.grid = 19;
    this.komi = 7.5;
    // The handicap rule (0 even, 1 让先, 2-9 stones) and the starting position the '.set' record
    // lays out ({ stones: [{ point, stone }], next }, or null: an empty board, black first).
    this.handicap = 0;
    this.position = null;
    // A finished game's request to be analysed ({ userId, time }), or null (SPEC §6.6).
    this.analysisRequest = null;
    // The open sealed move ({ n, index, stone, sealedBy, request }, without its move), and its move
    // for its sealer only (plan 2026-10-29-sealed-moves.md).
    this.seal = null;
    this.sealedPoint = null;
    this.board = new Board(this.grid);
    this.nextPlayer = Stone.BLACK;
    this.watchers = []
    this.#playableCacheMask = null;
    this.unplayableReason = null;
    this.moves = [];
  }

  load(data) {
    this.id = data.id || null,
    this.revision = data.revision || 0;
    this.players = data.players || [];
    this.status = 'in-progress';
    this.winner = Stone.parse(data.winner);
    this.countResult = data.countResult;
    this.grid = data.setup.grid;
    this.komi = typeof data.setup.komi === 'number' ? data.setup.komi : 7.5;
    this.handicap = data.setup.handicap || 0;
    // The AI's level in a game against it (plan 2026-10-28-ai-games.md); null in other games.
    this.aiLevel = data.setup.aiLevel || null;
    this.position = data.setup.position || null;
    this.analysisRequest = data.analysisRequest || null;
    this.seal = data.seal || null;
    this.sealedPoint = null;
    this.board = new Board(this.grid);
    this.nextPlayer = Stone.BLACK;
    this.watchers = []
    this.#playableCacheMask = null;
    this.unplayableReason = null;

    this.moves = [];
    for(const m of data.moves) {
      const move = this.#parseMove(m);
      const d = this.makeMove(move);
      if(!d) throw new Error(this.unplayableReason);
    }

    if(data.status) this.status = data.status;
  }

  #parseMove(m) {
    const move = {
      ...m,
      ...this.#getPoint({ code: m.move }),
      stone: Stone.parse(m.stone),
      variations: deepCopy(m.variations)
    };

    if(move.variations) {
      for(const v of move.variations) {
        const varMoves = [];
        for(const m of v.moves)
          varMoves.push(this.#parseMove(m));
        v.moves = varMoves;
      }
    }

    return move;
  }

  clone() {
    const g = new Game();
    g.id = this.id;
    g.status = this.status;
    g.winner = this.winner;
    g.countResult = deepCopy(this.countResult);
    g.grid = this.grid;
    g.komi = this.komi;
    g.handicap = this.handicap;
    g.position = deepCopy(this.position);
    g.analysisRequest = this.analysisRequest;
    g.board = this.board.clone();
    g.nextPlayer = this.nextPlayer;
    g.moves = deepCopy(this.moves);
    return g;
  }

  resetTo(index) {
    if(index > this.moves.length)
      return false;

    this.status = 'in-progress';
    while(this.moves.length > index)
      this.takeBack();

    return true;
  }

  get lastMove() {
    if(this.moves.length === 0)
      return null;
    else
      return this.moves[this.moves.length - 1];
  }

  // The open seal as the viewer sees it, and its move for its sealer (null for anyone else).
  setSeal(seal, point) {
    this.seal = seal;
    this.sealedPoint = point;
  }

  addWatcher(watcher) {
    this.watchers.push(watcher);
  }

  play(point) {
    if(this.moves.length === 0) {
      const ok = this.#makeNewMove({ move: '.set', stone: null });
      if(!ok) return false;
    }

    return this.#makeNewMove({ move: this.#getPointCode(point) });
  }

  resign() {
    return this.#makeNewMove({ move: '.resign' });
  }

  pass() {
    return this.#makeNewMove({ move: '.pass' });
  }

  #makeNewMove(move) {
    const newMove = {
      isLocalOrigin: true,
      index: this.moves.length,
      time: new Date(),
      stone: this.nextPlayer,
      ...move
    };

    return this.makeMove(newMove);
  }

  makeMove(move) {
    if(this.status !== 'in-progress') {
      this.unplayableReason = 'game not in progress';
      return false;
    }

    if(move.index !== this.moves.length) {
      this.unplayableReason = `invalid move index: ${JSON.stringify(move)}(moves.length=${this.moves.length})`;
      return false;
    }

    if(move.index === 0 && move.move !== '.set') {
      this.unplayableReason = `invalid first move: ${JSON.stringify(move)}`;
      return false;
    }

    if(move.index !== 0 && move.move === '.set') {
      this.unplayableReason = `invalid .set move: ${JSON.substring(move)}`;
      return false;
    }

    let moved = null;
    if(move.move.startsWith('.'))
      moved = this.#makeVirtualMove(move);
    else
      moved = this.#makeStoneMove(move);

    if(moved) {
      this.#finalizeMove(moved);
      return true;
    } else {
      return false;
    }
  }

  #makeStoneMove(move) {
    if(move.stone !== this.nextPlayer) {
      this.unplayableReason = `invalid stone: ${JSON.stringify(move)}(nextPlayer=${this.nextPlayer.toString()})`;
      return null;
    }

    const { x, y } = this.#getPoint({ code: move.move })
    if(!this.canPlay({ x, y })) return null;

    this.board.put(x, y, this.nextPlayer);
    this.#playableCacheMask = null;

    const killed = [];
    this.tryKillWith(x, y, { killed });

    return {...move, x, y, killed, isKilledBy: null};
  }

  #makeVirtualMove(move) {
    switch(move.move) {
      case '.set':
        return this.#makeSet(move);
      case '.pass':
        return this.#makePass(move);
      case '.resign':
        return this.#makeResign(move);
      default:
        throw new Error(`unknown virtual move: "${move}"`);
    }
  }

  // The '.set' record lays out the setup's starting position: its stones on the board, and the turn
  // to its first colour. The record itself holds no stones (DESIGN §2.3).
  // The stones placed are kept with the record (setStones, index 0) so that the board can draw them
  // and mark them when they are captured, as it does a move's stone.
  #makeSet(move) {
    const setStones = [];
    for(const s of (this.position && this.position.stones) || []) {
      const { x, y } = this.decode(s.point);
      const stone = Stone.parse(s.stone);
      this.board.put(x, y, stone);
      setStones.push({ index: 0, move: s.point, x, y, stone, isKilledBy: null, isSetStone: true });
    }
    if(setStones.length) this.#playableCacheMask = null;
    return { ...move, stone: null, setStones, next: this.firstPlayer };
  }

  // The colour that moves first, after the starting position.
  get firstPlayer() {
    return (this.position && this.position.next && Stone.parse(this.position.next)) || Stone.BLACK;
  }

  // Whether anything has been played: a record after the '.set'.
  get hasMoves() {
    return this.moves.some(m => m.move !== '.set');
  }

  #makePass(move) {
    // TODO: check two passes in a row
    return move;
  }

  #makeResign(move) {
    this.status = 'completed';
    this.winner = Game.invert(move.stone);
    return move;
  }

  #finalizeMove(move) {
    this.moves.push(move);

    if(move.killed)
      this.markKilledBy(move.index, move.killed);

    this.nextPlayer = move.next || Game.invert(this.nextPlayer);

    this.#notify(w => { if(w.onMove) w.onMove(move); });
    return move;
  }
  
  #notify(n) {
    for(const watcher of this.watchers) {
      n(watcher);
    }
  }

  #getPoint(point) {
    return point.code ? this.decode(point.code) : point;
  }

  #getPointCode(point) {
    return point.code ? point.code : this.encode(point.x, point.y);
  }

  decode(code) {
    let x = code.charCodeAt(0) - 65;
    if(x > 8) x--;
    const y = this.board.size - parseInt(code.substr(1), 10);
    return { x, y };
  }

  encode(x, y) {
    let c = x;
    if(c > 7) c++;
    return `${String.fromCharCode(c + 65)}${this.board.size - y}`;
  }

  takeBack() {
    if(this.moves.length === 0) return false;

    const lastMove = this.moves.pop();
    if(lastMove.killed) {
      this.markKilledBy(null, lastMove.killed);

      for(const k of lastMove.killed)
        this.board.put(k.x, k.y, Game.invert(lastMove.stone))
    }

    if(!lastMove.move.startsWith('.')) {
      this.board.put(lastMove.x, lastMove.y, Stone.NONE);
      this.#playableCacheMask = null;
    }

    if(lastMove.move === '.set') {
      // Back to the empty board before the setup.
      for(const p of lastMove.setStones || [])
        this.board.put(p.x, p.y, Stone.NONE);
      this.#playableCacheMask = null;
      this.nextPlayer = Stone.BLACK;
    } else {
      this.nextPlayer = Game.invert(this.nextPlayer);
    }

    this.#notify(w => { if(w.onTakeBack) w.onTakeBack(lastMove); });
    return true;
  }

  canPlay(point) {
    const { x, y } = this.#getPoint(point)

    let r = this.#getCachedPlayable(x, y);
    if(r !== null) return r;

    try {
      if(this.board.get(x, y) != Stone.NONE) {
        this.unplayableReason = 'cannot play on another stone';
        return r = false;
      }

      if(this.isSuiciding(x, y, this.nextPlayer)) {
        this.unplayableReason = 'cannot suicide';
        return r = false;
      }

      if(this.isAgainstKoRule(x, y)) {
        this.unplayableReason = 'cannot play against ko rule';
        return r = false;
      }

      return r = true;
    } finally {
      this.#cachePlayable(x, y, r);
    }
  }

  #getCachedPlayable(x, y) {
    if(this.#playableCacheMask) {
      const mark = this.#playableCacheMask.get(x, y);
      if(mark !== Stone.NONE)
        return mark === Stone.WHITE;  // WHITE for yes
    }

    return null;
  }

  #cachePlayable(x, y, playable) {
    if(!this.#playableCacheMask)
      this.#playableCacheMask = new Board(this.board.size);

    // WHITE for yes, BLACK for no
    this.#playableCacheMask.put(x, y, playable ? Stone.WHITE : Stone.BLACK);
  }

  isAgainstKoRule(x, y) {
    if(!this.lastMove || !this.killsOnly(this.lastMove.killed, x, y))
      return false;

    this.board.put(x, y, this.nextPlayer);
    try {
      const killed = [];
      this.tryKillWith(x, y, { noKill: true, killed });
      return this.killsOnly(killed, this.lastMove.x, this.lastMove.y);
    } finally {
      this.board.put(x, y, Stone.NONE);
    }
  }

  killsOnly(killed, x, y) {
    return killed && killed.length === 1 && killed[0].x === x && killed[0].y === y;
  }

  isSuiciding(x, y, color) {
    this.board.put(x, y, color);
    try {
      const canKill = this.tryKillWith(x, y, { noKill: true });
      return !canKill && this.isDead(x, y);
    } finally {
      this.board.put(x, y, Stone.NONE);
    }
  }

  tryKillWith(x, y, options={}) {
    const me = this.board.get(x, y);
    const op = Game.invert(me);
    let isKilling = false;

    this.getNeighbours({x, y})
      .forEach(n => {
        if(this.board.get(n.x, n.y) === op) {
          const chain = [];
          if(this.isDead(n.x, n.y, { chain })) {
            isKilling = true;
            if(!options.noKill) this.kill(chain);
            if(options.killed) options.killed.push(...chain);
          }
        }
      });

    return isKilling;
  }

  getChain(x, y, chain=[]) {
    const color = this.board.get(x, y);
    const mask = new Board(this.board.size);

    chain.push({ x, y });
    for(let i = chain.length - 1; i < chain.length; i++) {
      this.getNeighbours(chain[i])
        .filter(n => mask.get(n.x, n.y) === Stone.NONE && this.board.get(n.x, n.y) === color)
        .forEach(n => {
          mask.put(n.x, n.y, color);
          chain.push(n);
        });
    }

    return chain;
  }

  getNeighbours(p) {
    return [
      { x: p.x - 1, y: p.y },
      { x: p.x + 1, y: p.y },
      { x: p.x, y: p.y - 1 },
      { x: p.x, y: p.y + 1 }
    ];
  }

  isDead(x, y, options={}) {
    const chain = this.getChain(x, y, options.chain || []);
    return this.countLiberties(chain) === 0;
  }

  countLiberties(chain) {
    const mask = new Board(this.board.size, Stone.BLACK);

    let libs = 0;
    chain.forEach(s => {
      this.getNeighbours(s)
        .filter(n => mask.get(n.x, n.y) === Stone.BLACK && this.board.get(n.x, n.y) === Stone.NONE)
        .forEach(n => {
          mask.put(n.x, n.y, Stone.NONE);
          libs++;
        });
    });

    return libs;
  }

  kill(chain) {
    chain.forEach(s => {
      this.board.put(s.x, s.y, Stone.NONE);
    });
  }

  markKilledBy(k, chain) {
    chain.forEach(s => {
      for(let i = this.moves.length - 1; i >= 0; i--) {
        if(this.moves[i].x === s.x && this.moves[i].y === s.y) {
          this.moves[i].isKilledBy = k;
          return;
        }
      }
      // No move there: a stone of the starting position.
      const set = this.moves[0] && this.moves[0].setStones;
      const p = set && set.find(p => p.x === s.x && p.y === s.y);
      if(p) p.isKilledBy = k;
    });
  }

  static invert(s) {
    switch(s) {
      case Stone.BLACK:
        return Stone.WHITE;
      case Stone.WHITE:
        return Stone.BLACK;
      default:
        return Stone.NONE;
    }
  }

  get(x, y) {
    return this.board.get(x, y);
  }

  // SPEC §10: the game information, the starting position (AB/AW, PL) and the moves. Chinese rules;
  // a handicap game has HA, and SGF programs apply its N-point return themselves.
  getSgf() {
    const COORDS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let setup = this.handicap >= 2 ? `HA[${this.handicap}]` : '';
    setup += 'RU[Chinese]';
    if(this.position) {
      for(const colour of ['black', 'white']) {
        const points = (this.position.stones || []).filter(s => s.stone === colour).map(s => {
          const { x, y } = this.decode(s.point);
          return `[${COORDS[x]}${COORDS[y]}]`;
        });
        if(points.length) setup += `${colour === 'black' ? 'AB' : 'AW'}${points.join('')}`;
      }
      if(this.position.next) setup += `PL[${this.position.next === 'white' ? 'W' : 'B'}]`;
    }
    let sgf = `(;FF[4]SZ[${this.grid}]KM[${this.komi}]${setup}EV[SlowGo Game ${this.id}]\nPB[Black]PW[White]RE[${this.getSgfResult()}]\n`;

    for(const m of this.moves) {
      if(!m.move.startsWith('.'))
        sgf += this.getSgfMove(m);
    }

    sgf += ')';
    return sgf;
  }

  // The returned points of a handicap game (Handicap/2 子 = Handicap points); none for 让先.
  get handicapReturn() {
    return this.handicap >= 2 ? this.handicap : 0;
  }

  // Black's count less white's, komi and the handicap return, in points (SPEC §5.5): more than 0
  // black wins, less white, 0 is a draw.
  countBalance(result) {
    return result.black - result.white - this.komi - this.handicapReturn;
  }

  // RE: the winner and the margin in points of a counted game, R for a resignation, 0 for a draw.
  getSgfResult() {
    if(this.status !== 'completed')
      return 'Void';
    if(this.winner !== Stone.BLACK && this.winner !== Stone.WHITE)
      return this.winner === null ? '0' : '?';
    const w = this.winner === Stone.BLACK ? 'B' : 'W';
    if(!this.countResult)
      return `${w}+R`;
    return `${w}+${Math.abs(this.countBalance(this.countResult))}`;
  }

  getSgfMove(m) {
    const COORDS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if(m.x < 0 || m.x >= COORDS.length || m.y < 0 || m.y >= COORDS.length)
      throw new Error(`bad move coords for "${m}"`);

    const s = (m.stone === Stone.BLACK ? 'B' : 'W');
    // KataGo's lead for black after the move, as SGF's estimated score (SPEC §10).
    const a = m.analysis;
    const v = a && a.engine ? `V[${(Math.round((a.scoreLead || 0) * 10) / 10 + 0).toFixed(1)}]` : '';
    // A move sealed at a break and played when the game resumed (plan 2026-10-29-sealed-moves.md).
    const c = m.sealedAt ? 'C[封手]' : '';
    return `;${s}[${COORDS[m.x]}${COORDS[m.y]}]${v}${c}`;
  }
}
